import type { VehicleInsuranceIsCaLossPayeeEnum } from "@credit-acceptance/step6-bff-client";
import { PhoneIcon } from "lucide-react";
import type { ChangeEvent, KeyboardEvent } from "react";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import type { FieldError } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../../components/ui/select";
import { Checkbox } from "../../../../../components/ui/uikit/checkbox";
import { CurrencyInput } from "../../../../../components/ui/uikit/currencyInput";
import { DateInput } from "../../../../../components/ui/uikit/dateInput";
import { Input } from "../../../../../components/ui/uikit/input";
import { buildTelHref } from "../../Signers/fields/buildTelHref";
import type { FullCoverageValue } from "./InsuranceInformation.schema";

// ---------------------------------------------------------------------------
// Field wrappers for the Vehicle section — dual-mode, same as a native
// <input>'s value/defaultValue split:
//
// - `onChange` omitted (today, every current call site): the field is
//   uncontrolled — it owns its own local state, seeded/resynced from `value`.
//   This is what Vehicle needs today, since the BFF's dashboard save endpoint
//   doesn't persist vehicle/insurance/financial data — there's nowhere for a
//   real form to submit to.
// - `onChange` provided: the field is controlled — `value` is rendered
//   directly and every edit calls `onChange`, exactly like Signers'
//   SimpleTextField/SimpleDateField driven by `useController`. A future
//   RHF-wired Vehicle form plugs straight in:
//     const { field, fieldState } = useController({ control, name: "..." });
//     <TextField label={...} value={field.value} onChange={field.onChange} error={fieldState.error} />
//   `error` renders through a local, dependency-free message paragraph rather
//   than Signers' `FormMessage` — `FormMessage` unconditionally calls RHF's
//   `useFormContext()`, which throws once there's no ambient `<FormProvider>`
//   (i.e. no `<Form {...form}>` wrapping the tree, which Vehicle doesn't have
//   today), so it can't be reused here without forcing every caller — even
//   ones with no form at all — to wrap in one.
// ---------------------------------------------------------------------------

interface FieldErrorMessageProperties {
  readonly error?: FieldError;
}

function FieldErrorMessage({ error }: FieldErrorMessageProperties) {
  if (!error?.message) {
    return null;
  }
  return (
    <p role="alert" className="text-destructive text-2xs -mt-1">
      {String(error.message)}
    </p>
  );
}

/** Sentinel for "no caret restoration pending"; a real target is always a digit count >= 0. */
const NO_CARET = -1;

interface TextFieldProperties {
  readonly label: string;
  readonly value?: string;
  readonly onChange?: (value: string) => void;
  /** Forwarded to the underlying input regardless of controlled/uncontrolled — needed for RHF's `mode: "onBlur"` validation to ever run. */
  readonly onBlur?: () => void;
  readonly disabled?: boolean;
  readonly error?: FieldError;
  readonly maxLength?: number;
  /** When provided, the label renders as a clickable link instead of plain text (e.g. to open a modal or navigate) and this fires on click. */
  readonly onLabelClick?: () => void;
  /** Uppercases the value on blur/tab-out — matches dealer-ui behavior, same as Signers' SimpleTextField `uppercase` prop. */
  readonly uppercase?: boolean;
  /**
   * Formats the stored value for display (e.g. thousands separators). The field stays controlled on
   * the *stored* value, and the caret is preserved across reformatting, so inserting a digit
   * mid-string doesn't throw the cursor to the end. Pair with `parseValue`.
   */
  readonly formatValue?: (value: string) => string;
  /** Normalizes raw input back to the stored form value. Required whenever `formatValue` is set. */
  readonly parseValue?: (value: string) => string;
}

/** Counts the digits in a string — the caret anchor that survives reformatting, unlike a raw offset. */
function countDigits(text: string): number {
  return (text.match(/\d/g) ?? []).length;
}

export function TextField({
  label,
  value,
  onChange,
  onBlur,
  disabled = true,
  error,
  maxLength,
  onLabelClick,
  uppercase,
  formatValue,
  parseValue,
}: TextFieldProperties) {
  const inputId = useId();
  const labelId = useId();
  const isControlled = onChange !== undefined;
  const [localValue, setLocalValue] = useState(value ?? "");
  useEffect(() => {
    if (!isControlled) {
      setLocalValue(value ?? "");
    }
  }, [value, isControlled]);
  const storedValue = isControlled ? (value ?? "") : localValue;
  const displayValue = formatValue ? formatValue(storedValue) : storedValue;

  const inputReference = useRef<HTMLInputElement>(null);
  // Digits to the left of the caret, stashed on edit and reapplied once the reformatted value has
  // rendered. NO_CARET means "don't touch the caret" — e.g. a re-render the user didn't cause.
  const caretDigitsReference = useRef<number>(NO_CARET);

  useLayoutEffect(() => {
    const input = inputReference.current;
    const targetDigits = caretDigitsReference.current;
    caretDigitsReference.current = NO_CARET;
    if (!input || targetDigits === NO_CARET) {
      return;
    }
    let seen = 0;
    let position = 0;
    while (position < displayValue.length && seen < targetDigits) {
      if (/\d/.test(displayValue[position])) {
        seen += 1;
      }
      position += 1;
    }
    input.setSelectionRange(position, position);
  }, [displayValue]);

  function commit(nextRaw: string) {
    const next = parseValue ? parseValue(nextRaw) : nextRaw;
    if (isControlled) {
      onChange(next);
    } else {
      setLocalValue(next);
    }
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const raw = event.target.value;
    if (formatValue) {
      caretDigitsReference.current = countDigits(raw.slice(0, event.target.selectionStart ?? raw.length));
    }
    commit(raw);
  }

  // Backspacing onto a separator would otherwise look like a no-op: the character is removed, then
  // reformatting puts it straight back. Delete the digit in front of it instead.
  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    const input = event.currentTarget;
    const caret = input.selectionStart ?? 0;
    if (
      !formatValue ||
      event.key !== "Backspace" ||
      caret !== input.selectionEnd ||
      caret < 2 ||
      /\d/.test(input.value[caret - 1])
    ) {
      return;
    }
    event.preventDefault();
    const nextRaw = input.value.slice(0, caret - 2) + input.value.slice(caret);
    caretDigitsReference.current = countDigits(nextRaw.slice(0, caret - 2));
    commit(nextRaw);
  }

  function handleBlur() {
    if (uppercase) {
      const upper = displayValue.toUpperCase();
      if (upper !== displayValue) {
        if (isControlled) {
          onChange?.(upper);
        } else {
          setLocalValue(upper);
        }
      }
    }
    onBlur?.();
  }

  return (
    <div className="flex flex-col gap-1">
      {onLabelClick ? (
        <button
          type="button"
          id={labelId}
          onClick={onLabelClick}
          className="text-primary text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-medium) w-fit text-left underline underline-offset-2"
        >
          {label}
        </button>
      ) : (
        <label
          htmlFor={inputId}
          className="text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-medium)"
        >
          {label}
        </label>
      )}
      <Input
        id={inputId}
        ref={inputReference}
        type="text"
        value={displayValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        disabled={disabled}
        maxLength={maxLength}
        aria-invalid={Boolean(error) || undefined}
        aria-labelledby={onLabelClick ? labelId : undefined}
        className="w-full"
      />
      <FieldErrorMessage error={error} />
    </div>
  );
}

interface CurrencyFieldProperties {
  readonly label: string;
  readonly value?: number | null;
  readonly onChange?: (value: number | undefined) => void;
  /** Forwarded to the underlying input regardless of controlled/uncontrolled — needed for RHF's `mode: "onBlur"` validation to ever run. */
  readonly onBlur?: () => void;
  readonly disabled?: boolean;
  readonly error?: FieldError;
  readonly maxLength?: number;
}

export function CurrencyField({
  label,
  value,
  onChange,
  onBlur,
  disabled = true,
  error,
  maxLength,
}: CurrencyFieldProperties) {
  const inputId = useId();
  const isControlled = onChange !== undefined;
  const [localValue, setLocalValue] = useState(value);
  useEffect(() => {
    if (!isControlled) {
      setLocalValue(value);
    }
  }, [value, isControlled]);
  const displayValue = isControlled ? value : localValue;

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={inputId} className="text-xs font-medium">
        {label}
      </label>
      <CurrencyInput
        id={inputId}
        value={displayValue}
        onChange={isControlled ? onChange : setLocalValue}
        onBlur={onBlur}
        disabled={disabled}
        maxLength={maxLength}
        aria-invalid={Boolean(error) || undefined}
        className="w-full"
      />
      <FieldErrorMessage error={error} />
    </div>
  );
}

interface DateFieldProperties {
  readonly label: string;
  readonly value?: string;
  readonly onChange?: (value: string) => void;
  /** Forwarded to the underlying input regardless of controlled/uncontrolled — needed for RHF's `mode: "onBlur"` validation to ever run. */
  readonly onBlur?: () => void;
  readonly disabled?: boolean;
  readonly error?: FieldError;
}

export function DateField({ label, value, onChange, onBlur, disabled = true, error }: DateFieldProperties) {
  const inputId = useId();
  const isControlled = onChange !== undefined;
  const [localValue, setLocalValue] = useState(value ?? "");
  useEffect(() => {
    if (!isControlled) {
      setLocalValue(value ?? "");
    }
  }, [value, isControlled]);
  const displayValue = isControlled ? (value ?? "") : localValue;

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={inputId} className="text-xs font-medium">
        {label}
      </label>
      <DateInput
        id={inputId}
        value={displayValue}
        onChange={isControlled ? onChange : setLocalValue}
        onBlur={onBlur}
        disabled={disabled}
        aria-invalid={Boolean(error) || undefined}
      />
      <FieldErrorMessage error={error} />
    </div>
  );
}

interface CheckboxFieldProperties {
  readonly label: string;
  readonly checked?: boolean;
  readonly onCheckedChange?: (checked: boolean) => void;
  readonly disabled?: boolean;
}

/** Never validated — no `error`/`FieldErrorMessage`, unlike the other fields here. */
export function CheckboxField({ label, checked, onCheckedChange, disabled = true }: CheckboxFieldProperties) {
  const inputId = useId();
  return (
    <div className="flex items-center gap-2">
      <Checkbox id={inputId} checked={Boolean(checked)} onCheckedChange={onCheckedChange} disabled={disabled} />
      <label htmlFor={inputId} className="text-xs font-medium">
        {label}
      </label>
    </div>
  );
}

interface PhoneFieldProperties {
  readonly label: string;
  readonly value?: string;
  readonly onChange?: (value: string) => void;
  /** Forwarded to the underlying input regardless of controlled/uncontrolled — needed for RHF's `mode: "onBlur"` validation to ever run. */
  readonly onBlur?: () => void;
  readonly disabled?: boolean;
  readonly error?: FieldError;
}

/** Mirrors Signers' PhoneRow call-icon markup, minus the type select/extension — insurance phone is a single formatted string. The call icon dials whenever a number is present, independent of `disabled` — dialing isn't editing. */
export function PhoneField({ label, value, onChange, onBlur, disabled = true, error }: PhoneFieldProperties) {
  const { t } = useTranslation();
  const inputId = useId();
  const isControlled = onChange !== undefined;
  const [localValue, setLocalValue] = useState(value ?? "");
  useEffect(() => {
    if (!isControlled) {
      setLocalValue(value ?? "");
    }
  }, [value, isControlled]);
  const displayValue = isControlled ? (value ?? "") : localValue;
  const digits = displayValue.replaceAll(/\D/g, "");

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    if (isControlled) {
      onChange(event.target.value);
    } else {
      setLocalValue(event.target.value);
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={inputId} className="text-xs font-medium">
        {label}
      </label>
      <div className="flex items-center gap-1">
        <Input
          id={inputId}
          type="text"
          value={displayValue}
          onChange={handleChange}
          onBlur={onBlur}
          disabled={disabled}
          aria-invalid={Boolean(error) || undefined}
          className="w-full"
        />
        {digits ? (
          <a
            href={buildTelHref(digits)}
            aria-label={t("vehicle.insurance.call")}
            className="text-success-foreground border-success-foreground hover:bg-success flex size-6 shrink-0 items-center justify-center rounded-md border transition-colors"
          >
            <PhoneIcon className="size-3.5 fill-current" aria-hidden="true" />
          </a>
        ) : (
          <span
            aria-hidden="true"
            className="text-muted-foreground border-muted flex size-6 shrink-0 items-center justify-center rounded-md border opacity-60"
          >
            <PhoneIcon className="size-3.5 fill-current" aria-hidden="true" />
          </span>
        )}
      </div>
      <FieldErrorMessage error={error} />
    </div>
  );
}

const LOSS_PAYEE_UNSET = "__unset__";

interface LossPayeeFieldProperties {
  readonly label: string;
  readonly value?: VehicleInsuranceIsCaLossPayeeEnum;
  readonly onChange?: (value: VehicleInsuranceIsCaLossPayeeEnum) => void;
  readonly disabled?: boolean;
  readonly error?: FieldError;
}

/**
 * A disabled Radix Select ignores its own value-change lifecycle for
 * registering the selected item's display text, so a value set before the
 * user ever interacts with it renders a blank trigger without a `key` change
 * forcing a remount — same issue/fix as CustomerInformation's
 * SimpleSelectField. The remount key only applies while `disabled` — while
 * editable, the Select handles its own trigger-label updates on selection,
 * and remounting on every change would drop the just-opened dropdown's own
 * DOM node mid-interaction.
 */
export function LossPayeeField({ label, value, onChange, disabled = true, error }: LossPayeeFieldProperties) {
  const { t } = useTranslation();
  const selectId = useId();
  const options: Array<{ value: VehicleInsuranceIsCaLossPayeeEnum; label: string }> = [
    { value: "Y", label: t("vehicle.insurance.lossPayeeYes") },
    { value: "N", label: t("vehicle.insurance.lossPayeeNo") },
    { value: "U", label: t("vehicle.insurance.lossPayeeUnknown") },
  ];
  const isControlled = onChange !== undefined;
  const [localValue, setLocalValue] = useState(value ?? LOSS_PAYEE_UNSET);
  useEffect(() => {
    if (!isControlled) {
      setLocalValue(value ?? LOSS_PAYEE_UNSET);
    }
  }, [value, isControlled]);
  const displayValue = isControlled ? (value ?? LOSS_PAYEE_UNSET) : localValue;

  function handleValueChange(next: string) {
    if (isControlled) {
      onChange(next as VehicleInsuranceIsCaLossPayeeEnum);
    } else {
      setLocalValue(next as VehicleInsuranceIsCaLossPayeeEnum);
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={selectId} className="text-xs font-medium">
        {label}
      </label>
      <Select
        key={disabled ? displayValue : "editable"}
        value={displayValue}
        onValueChange={handleValueChange}
        disabled={disabled}
      >
        <SelectTrigger id={selectId} aria-label={label} disabled={disabled} aria-invalid={Boolean(error) || undefined}>
          <SelectValue placeholder={t("vehicle.insurance.lossPayeeUnknown")} />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <FieldErrorMessage error={error} />
    </div>
  );
}

const FULL_COVERAGE_UNSET = "__unset__";

interface FullCoverageFieldProperties {
  readonly label: string;
  readonly value?: FullCoverageValue;
  readonly onChange?: (value: FullCoverageValue) => void;
  readonly disabled?: boolean;
}

/**
 * Yes/No dropdown over `VehicleInsurance.fullCoverage` (see
 * InsuranceInformation.schema.ts), structured identically to
 * {@link LossPayeeField} (same disabled-Select remount fix).
 * Never validated, so no `error` prop.
 */
export function FullCoverageField({ label, value, onChange, disabled = true }: FullCoverageFieldProperties) {
  const { t } = useTranslation();
  const selectId = useId();
  const options: Array<{ value: FullCoverageValue; label: string }> = [
    { value: "Y", label: t("vehicle.insurance.fullCoverageYes") },
    { value: "N", label: t("vehicle.insurance.fullCoverageNo") },
  ];
  const isControlled = onChange !== undefined;
  const [localValue, setLocalValue] = useState(value ?? FULL_COVERAGE_UNSET);
  useEffect(() => {
    if (!isControlled) {
      setLocalValue(value ?? FULL_COVERAGE_UNSET);
    }
  }, [value, isControlled]);
  const displayValue = isControlled ? (value ?? FULL_COVERAGE_UNSET) : localValue;

  function handleValueChange(next: string) {
    if (isControlled) {
      onChange(next as FullCoverageValue);
    } else {
      setLocalValue(next as FullCoverageValue);
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={selectId} className="text-xs font-medium">
        {label}
      </label>
      <Select
        key={disabled ? displayValue : "editable"}
        value={displayValue}
        onValueChange={handleValueChange}
        disabled={disabled}
      >
        <SelectTrigger id={selectId} aria-label={label} disabled={disabled}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

const YES_NO_UNSET = "__unset__";

interface YesNoFieldProperties {
  readonly label: string;
  readonly value?: boolean;
  readonly onChange?: (value: boolean) => void;
  readonly disabled?: boolean;
  readonly error?: FieldError;
}

/** Same disabled-Select-with-forced-remount pattern as {@link LossPayeeField}, for a plain boolean field (no third "Unknown" state — `undefined` just renders blank). */
export function YesNoField({ label, value, onChange, disabled = true, error }: YesNoFieldProperties) {
  const { t } = useTranslation();
  const selectId = useId();
  const options = [
    { value: "true", label: t("vehicle.insurance.yes") },
    { value: "false", label: t("vehicle.insurance.no") },
  ];
  const isControlled = onChange !== undefined;
  const [localValue, setLocalValue] = useState(value === undefined ? YES_NO_UNSET : String(value));
  useEffect(() => {
    if (!isControlled) {
      setLocalValue(value === undefined ? YES_NO_UNSET : String(value));
    }
  }, [value, isControlled]);
  const displayValue = isControlled ? (value === undefined ? YES_NO_UNSET : String(value)) : localValue;

  function handleValueChange(next: string) {
    if (isControlled) {
      onChange(next === "true");
    } else {
      setLocalValue(next);
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={selectId} className="text-xs font-medium">
        {label}
      </label>
      <Select
        key={disabled ? displayValue : "editable"}
        value={displayValue}
        onValueChange={handleValueChange}
        disabled={disabled}
      >
        <SelectTrigger id={selectId} aria-label={label} disabled={disabled} aria-invalid={Boolean(error) || undefined}>
          <SelectValue placeholder="" />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <FieldErrorMessage error={error} />
    </div>
  );
}

interface DropdownFieldProperties {
  readonly label: string;
  readonly value?: string;
  readonly onChange?: (value: string) => void;
  /** Forwarded to the underlying trigger regardless of controlled/uncontrolled — needed for RHF's `mode: "onBlur"` validation to ever run. */
  readonly onBlur?: () => void;
  readonly options: Array<{ value: string; label: string }>;
  readonly disabled?: boolean;
  readonly error?: FieldError;
}

/**
 * Generic version of {@link LossPayeeField}/{@link FullCoverageField} for a
 * dynamically-built option list (e.g. VSC's TPAP Vendor/Form Number, mocked in
 * place of a live vendor-lookup service) — same disabled-Select remount fix,
 * no fixed `UNSET` sentinel of its own since callers supply whatever
 * placeholder/"not listed" entries their own option list needs.
 */
export function DropdownField({
  label,
  value,
  onChange,
  onBlur,
  options,
  disabled = true,
  error,
}: DropdownFieldProperties) {
  const selectId = useId();
  const isControlled = onChange !== undefined;
  const [localValue, setLocalValue] = useState(value ?? "");
  useEffect(() => {
    if (!isControlled) {
      setLocalValue(value ?? "");
    }
  }, [value, isControlled]);
  const displayValue = isControlled ? (value ?? "") : localValue;

  function handleValueChange(next: string) {
    if (isControlled) {
      onChange(next);
    } else {
      setLocalValue(next);
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={selectId} className="text-xs font-medium">
        {label}
      </label>
      <Select
        key={disabled ? displayValue : "editable"}
        value={displayValue}
        onValueChange={handleValueChange}
        disabled={disabled}
      >
        <SelectTrigger
          id={selectId}
          aria-label={label}
          disabled={disabled}
          aria-invalid={Boolean(error) || undefined}
          onBlur={onBlur}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <FieldErrorMessage error={error} />
    </div>
  );
}
